import { CheckIcon, CopyIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { formatCode } from "../../../../shared/uktzed.ts";
import { Button } from "@/components/ui/button.tsx";
import { Tooltip } from "@/components/ui/tooltip.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { cn, copyText } from "@/lib/utils.ts";

/** A UKTZED code printed as in the tariff (`8482 10 90 00`), mono with tabular digits. */
export function Code({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return null;
  return <span className={cn("code-mono whitespace-nowrap", className)}>{formatCode(value)}</span>;
}

/** Copies a value and shows a check for a moment. */
export function CopyButton({
  value,
  label,
  copiedLabel,
  className,
  size = "icon-xs",
  onCopied,
}: {
  value: string;
  label: string;
  copiedLabel?: string;
  className?: string;
  size?: "icon-xs" | "icon-sm" | "icon";
  onCopied?: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);
  const text = copied ? (copiedLabel ?? t.common.copied) : label;
  return (
    <Tooltip content={text}>
      <Button
        variant="ghost-muted"
        size={size}
        className={className}
        aria-label={text}
        onClick={async () => {
          if (await copyText(value)) {
            setCopied(true);
            onCopied?.();
          }
        }}
      >
        {copied ? <CheckIcon className="text-success" /> : <CopyIcon />}
      </Button>
    </Tooltip>
  );
}
